import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { TextShimmer } from "../motion-primitives/text-shimmer";
import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from "lucide-react";
import { motion } from "motion/react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const commonStyles =
  "text-sm text-muted-foreground/90 tracking-tight font-medium select-none";

const toolCallStyles =
  "text-[11px] font-mono font-normal text-muted-foreground/80 select-none";

export type ActionProps = {
  id: string;
  children: ReactNode;
  className?: string;
  isInProgress?: boolean;
};

export function Action({ id, children, className, isInProgress }: ActionProps) {
  // Don't apply shimmer to individual tool calls since the group header has it
  return (
    <div className={cn(toolCallStyles, className)} key={id}>
      {children}
    </div>
  );
}

export type ReasoningProps = {
  text: string;
  isThinking: boolean;
  className?: string;
};

const WORDS_PER_SECOND = 20;
const calculateDurationString = (text: string) => {
  const words = text.split(" ").length;
  const durationSeconds = Math.ceil(words / WORDS_PER_SECOND);
  return `${durationSeconds}s`;
};

export function Reasoning({ text, isThinking, className }: ReasoningProps) {
  const [isOpen, setIsOpen] = useState(false);
  const prevIsThinkingRef = useRef(isThinking);

  useEffect(() => {
    // Only run if transitioning from true -> false
    if (prevIsThinkingRef.current && !isThinking) {
      setTimeout(() => {
        setIsOpen(false);
      }, 1500);
    }

    if (isThinking) {
      setIsOpen(true);
    }

    prevIsThinkingRef.current = isThinking;
  }, [isThinking]);

  if (text.length === 0) {
    return null;
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(commonStyles, className)}
    >
      <CollapsibleTrigger className="cursor-pointer flex items-center gap-1">
        <div>
          {isThinking ? (
            <TextShimmer>Thinking...</TextShimmer>
          ) : (
            <div className={cn(commonStyles)}>
              Thought for {calculateDurationString(text)}
            </div>
          )}
        </div>
        {isOpen ? (
          <ChevronDownIcon className="size-3.5" />
        ) : (
          <ChevronRightIcon className="size-3.5" />
        )}
      </CollapsibleTrigger>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="overflow-hidden text-muted-foreground/80"
      >
        <CollapsibleContent
          className="text-sm flex flex-col gap-2 mt-2 whitespace-pre-line"
          forceMount
        >
          {text}
        </CollapsibleContent>
      </motion.div>
    </Collapsible>
  );
}

export function Tool({
  isInProgress,
  children,
}: {
  isInProgress: boolean;
  children?: React.ReactNode;
}) {
  if (isInProgress) {
    return (
      <TextShimmer
        duration={2}
        spread={10}
        className={cn(commonStyles, "flex gap-2 items-center")}
      >
        {children as string}
      </TextShimmer>
    );
  } else {
    return (
      <div className={cn(commonStyles, "flex items-center gap-2")}>
        {children}
      </div>
    );
  }
}
